import React from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes, css } from 'styled-components';
import {
    CheckCircleOutline,
    HighlightOff,
    ErrorOutline,
    InfoOutlined
} from '@material-ui/icons';
import { lightTheme as theme } from '../../theme';

// Animations
const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const slideUp = keyframes`
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
`;

// Styles
const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    animation: ${fadeIn} 0.2s ease-out;
    padding: 16px;
`;

const ModalCard = styled.div`
    background: ${theme.colors.paper};
    width: 100%;
    max-width: ${({ maxWidth }) => maxWidth || '400px'};
    max-height: calc(100vh - 48px);
    border-radius: 16px;
    box-shadow: ${theme.colors.shadowLg};
    padding: 24px;
    display: flex;
    flex-direction: column;
    align-items: ${({ align }) => (align === 'left' ? 'stretch' : 'center')};
    text-align: ${({ align }) => (align === 'left' ? 'left' : 'center')};
    animation: ${slideUp} 0.3s ease-out;
    position: relative;
    overflow: hidden;
`;

const IconWrapper = styled.div`
    width: 64px;
    height: 64px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    
    ${({ type }) => {
        switch (type) {
            case 'success':
                return css`background: ${theme.colors.successBg}; color: ${theme.colors.success};`;
            case 'error':
                return css`background: ${theme.colors.dangerBg}; color: ${theme.colors.danger};`;
            case 'warning':
                return css`background: ${theme.colors.warningBg}; color: ${theme.colors.warning};`;
            case 'info':
            default:
                return css`background: ${theme.colors.infoBg}; color: ${theme.colors.info};`;
        }
    }}

    svg {
        font-size: 32px;
    }
`;

const Title = styled.h2`
    margin: 0 0 12px 0;
    font-size: 20px;
    font-weight: 700;
    color: ${theme.colors.text};
    line-height: 1.3;
`;

const Message = styled.p`
    margin: 0 0 24px 0;
    font-size: 15px;
    color: ${theme.colors.textSecondary};
    line-height: 1.5;
`;

const Content = styled.div`
    width: 100%;
    margin-bottom: 24px;
    overflow-y: auto;
    flex: 1;

    /* Custom scrollbar for better appearance */
    &::-webkit-scrollbar {
        width: 6px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background: ${theme.colors.gray300};
        border-radius: 3px;
    }
`;

const ButtonRow = styled.div`
    display: flex;
    gap: 12px;
    width: 100%;
`;

const Button = styled.button`
    flex: 1;
    height: 44px;
    border-radius: 999px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    /* Variant Styles */
    ${({ variant, type }) => {
        // Secondary / Cancel Button
        if (variant === 'secondary') {
            return css`
                background: transparent;
                border: 1px solid ${theme.colors.gray300};
                color: ${theme.colors.textSecondary};
                &:hover { background: ${theme.colors.gray50}; }
            `;
        }

        // Primary / Confirm Button
        switch (type) {
            case 'success':
                return css`
                    background: ${theme.colors.success}; 
                    border: 0; 
                    color: white; 
                    &:hover { background: #236c4a; box-shadow: ${theme.colors.actionShadow}; }
                `;
            case 'error':
                return css`
                    background: ${theme.colors.danger}; 
                    border: 0; 
                    color: white; 
                    &:hover { background: #821b25; }
                `;
            case 'warning':
                return css`
                    background: ${theme.colors.warning}; 
                    border: 0;
                    color: white; 
                    &:hover { background: #b45309; }
                `;
            case 'info':
            default:
                return css`
                    background: ${theme.colors.action}; 
                    border: 0;
                    color: white; 
                    &:hover { background: #00acc1; box-shadow: ${theme.colors.actionShadow}; }
                `;
        }
    }}
`;

// Icon Mapping
const getIcon = (type) => {
    switch (type) {
        case 'success': return <CheckCircleOutline />;
        case 'error': return <HighlightOff />;
        case 'warning': return <ErrorOutline />;
        case 'info': default: return <InfoOutlined />;
    }
};

const Modal = ({
    isOpen,
    type,
    title,
    message,
    onConfirm,
    onCancel,
    confirmText,
    cancelText,
    showIcon,
    children,
    footer,
    maxWidth,
    align
}) => {
    if (!isOpen) return null;

    // Close on overlay click if onCancel is provided
    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget && onCancel) {
            onCancel();
        }
    };

    return (
        <Overlay onClick={handleOverlayClick}>
            <ModalCard maxWidth={maxWidth} align={align}>
                {showIcon && (
                    <IconWrapper type={type}>
                        {getIcon(type)}
                    </IconWrapper>
                )}

                {title ? <Title>{title}</Title> : null}
                {message ? <Message>{message}</Message> : null}
                {children ? <Content>{children}</Content> : null}

                {footer ? (
                    <ButtonRow>{footer}</ButtonRow>
                ) : (
                    <ButtonRow>
                        {onCancel && (
                            <Button variant="secondary" onClick={onCancel}>
                                {cancelText || 'Cancelar'}
                            </Button>
                        )}

                        {onConfirm && (
                            <Button type={type} onClick={onConfirm}>
                                {confirmText || 'Confirmar'}
                            </Button>
                        )}
                    </ButtonRow>
                )}
            </ModalCard>
        </Overlay>
    );
};

Modal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    type: PropTypes.oneOf(['success', 'error', 'warning', 'info']),
    title: PropTypes.string,
    message: PropTypes.node,
    onConfirm: PropTypes.func,
    onCancel: PropTypes.func,
    confirmText: PropTypes.string,
    cancelText: PropTypes.string,
    showIcon: PropTypes.bool,
    children: PropTypes.node,
    footer: PropTypes.node,
    maxWidth: PropTypes.string,
    align: PropTypes.oneOf(['left', 'center']),
};

Modal.defaultProps = {
    type: 'info',
    title: '',
    message: '',
    showIcon: true,
    children: null,
    footer: null,
    maxWidth: '400px',
    align: 'center',
};

export default Modal;
